import React, { useState, useEffect, useCallback, useContext } from "react"
import { auth, db, storage } from "../../utils/firebase"
import firebase from "firebase"
import moment from "moment"
import { ModalContext } from "../../utils/providers/modal"
import "./parsing-queue.scss"

// UI components
import Tile from "../../components/structure/tile/tile"
import Title from "../../components/structure/title/title"
import Button from "../../components/ui/button/button"
import Table from "../../components/structure/table/table"
import Badge from "../../components/ui/badge/badge"
import Input from "../../components/ui/inputs/input"
import Window from "../../components/structure/window/window"
import { Upload, FileText, X, ExternalLink, Plus, RotateCcwIcon, Trash2Icon } from "lucide-react"

// Returns the HTML component for Parsing Queue
export default function ParsingQueue() {
	const { showModal } = useContext(ModalContext)
	const [uploadedFile, setUploadedFile] = useState(null)
	const [isDragOver, setIsDragOver] = useState(false)
	const [isUploading, setIsUploading] = useState(false)
	const [uploadProgress, setUploadProgress] = useState(0)
	const [documents, setDocuments] = useState([])
	const [currentPage, setCurrentPage] = useState(1)
	const [itemsPerPage] = useState(20)
	const [agents, setAgents] = useState({})

	// Modal state management
	const [activeDocument, setActiveDocument] = useState(null)
	const [referenceInput, setReferenceInput] = useState("")
	const [validatingReference, setValidatingReference] = useState(false)
	const [referenceError, setReferenceError] = useState("")
	const [tempReferences, setTempReferences] = useState([])
	const [savingReferences, setSavingReferences] = useState(false)

	// Track which documents have expanded bookings list
	const [expandedBookings, setExpandedBookings] = useState({})

	// Track which document is being reset
	const [resettingDocumentId, setResettingDocumentId] = useState(null)

	// Fetch all agents on component mount
	useEffect(() => {
		db.collection("users")
			.orderBy("first_name")
			.get()
			.then((agentDocs) => {
				let agentsArr = {}
				agentDocs.forEach((agentDoc) => {
					const { first_name, last_name } = agentDoc.data()
					agentsArr[agentDoc.id] = `${first_name} ${last_name}`
				})
				setAgents(agentsArr)
			})
			.catch((error) => {
				console.error("Error fetching agents:", error)
			})
	}, [])

	// Setup real-time listener for documents on component mount
	useEffect(() => {
		const unsubscribe = db
			.collection("parsing_queue")
			.orderBy("uploaded_at", "desc")
			.limit(100)
			.onSnapshot((snapshot) => {
				const docs = []
				snapshot.forEach((doc) => {
					docs.push({ id: doc.id, ...doc.data() })
				})
				setDocuments(docs)
			})

		// Cleanup listener on unmount
		return () => unsubscribe()
	}, [])

	// Format file size for display
	const formatFileSize = (bytes) => {
		if (bytes === 0) return "0 Bytes"
		const k = 1024
		const sizes = ["Bytes", "KB", "MB", "GB"]
		const i = Math.floor(Math.log(bytes) / Math.log(k))
		return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i]
	}

	// Handle file drop
	const handleDrop = useCallback((e) => {
		e.preventDefault()
		setIsDragOver(false)

		const files = Array.from(e.dataTransfer.files)
		const pdfFile = files.find((file) => file.type === "application/pdf")

		if (pdfFile) {
			setUploadedFile({
				file_name: pdfFile.name,
				file_size: pdfFile.size,
				file_type: pdfFile.type,
				file: pdfFile,
				uploaded_at: new Date().toISOString(),
			})
		} else {
			alert("Please upload a PDF file only.")
		}
	}, [])

	// Handle drag over
	const handleDragOver = useCallback((e) => {
		e.preventDefault()
		setIsDragOver(true)
	}, [])

	// Handle drag leave
	const handleDragLeave = useCallback((e) => {
		e.preventDefault()
		setIsDragOver(false)
	}, [])

	// Handle file input change
	const handleFileChange = (e) => {
		const file = e.target.files[0]
		if (file && file.type === "application/pdf") {
			setUploadedFile({
				file_name: file.name,
				file_size: file.size,
				file_type: file.type,
				file: file,
				uploaded_at: new Date().toISOString(),
			})
		} else if (file) {
			alert("Please upload a PDF file only.")
		}
	}

	// Remove uploaded file
	const removeFile = () => {
		setUploadedFile(null)
		setIsUploading(false)
		setIsDragOver(false)
		setUploadProgress(0)
	}

	// Upload the file to storage and create database entry
	const uploadFile = async () => {
		if (!uploadedFile) return

		setIsUploading(true)
		setUploadProgress(0)

		try {
			// Generate unique ID for the document
			const documentId = db.collection("parsing_queue").doc().id
			const year = moment().format("YYYY")
			const month = moment().format("MM")
			const fileName = `${documentId}_${uploadedFile.file_name}`

			// Upload to Firebase Storage
			const uploadTask = storage.ref(`parsing_queue/${year}/${month}/${fileName}`).put(uploadedFile.file)

			// Monitor upload progress
			uploadTask.on(
				"state_changed",
				(snapshot) => {
					const progress = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100)
					setUploadProgress(progress)
				},
				(error) => {
					console.error("Upload error:", error)
					alert("Error uploading file: " + error.message)
					setIsUploading(false)
				},
				async () => {
					// Get download URL
					const downloadURL = await uploadTask.snapshot.ref.getDownloadURL()

					// Save document metadata to Firestore
					await db
						.collection("parsing_queue")
						.doc(documentId)
						.set({
							file_name: uploadedFile.file_name,
							file_size: uploadedFile.file_size,
							file_type: uploadedFile.file_type,
							file_url: downloadURL,
							file_path: `parsing_queue/${year}/${month}/${fileName}`,
							uploaded_by_email: null,
							uploaded_by_uid: auth.currentUser.uid,
							uploaded_at: firebase.firestore.FieldValue.serverTimestamp(),
							status: "pending",
						})

					// Reset form
					setUploadedFile(null)
					setIsUploading(false)
					setUploadProgress(0)
				}
			)
		} catch (error) {
			console.error("Error uploading file:", error)
			alert("Error uploading file: " + error.message)
			setIsUploading(false)
		}
	}

	// Validate booking reference exists in database
	const validateBookingReference = async (reference) => {
		setValidatingReference(true)
		setReferenceError("")

		try {
			// Try to find booking with this reference
			let booking = await db.collection("bookings").where("reference", "==", reference).limit(1).get()

			// If not found, try parsing as integer
			if (booking.empty && !isNaN(Number(reference))) {
				booking = await db.collection("bookings").where("reference", "==", Number(reference)).limit(1).get()
			}

			if (!booking.empty) {
				const bookingData = booking.docs[0].data()
				setValidatingReference(false)
				return {
					booking_id: booking.docs[0].id,
					reference: bookingData.reference,
				}
			} else {
				setReferenceError("Booking reference not found")
				setValidatingReference(false)
				return null
			}
		} catch (error) {
			console.error("Error validating reference:", error)
			setReferenceError("Error validating reference")
			setValidatingReference(false)
			return null
		}
	}

	// Add a validated reference to temporary list
	const addReferenceToList = async () => {
		if (!referenceInput.trim()) {
			setReferenceError("Please enter a booking reference")
			return
		}

		// Check if already in temp list
		if (tempReferences.find((ref) => ref.reference === Number(referenceInput))) {
			setReferenceError("Reference already added")
			return
		}

		const validatedRef = await validateBookingReference(referenceInput)
		if (validatedRef) {
			setTempReferences([...tempReferences, validatedRef])
			setReferenceInput("")
			setReferenceError("")
		}
	}

	// Remove reference from temporary list
	const removeReferenceFromList = (reference) => {
		setTempReferences(tempReferences.filter((ref) => ref.reference !== reference))
	}

	// Save references to Firestore
	const saveBookingReferences = async (documentId) => {
		if (tempReferences.length === 0) {
			setReferenceError("Please add at least one booking reference")
			return
		}

		setSavingReferences(true)

		try {
			// Update the document with the complete list of references
			await db.collection("parsing_queue").doc(documentId).update({
				matched_bookings: tempReferences,
			})

			// Reset state and close modal
			closeModal()
			setSavingReferences(false)
		} catch (error) {
			console.error("Error saving references:", error)
			setReferenceError("Error saving references")
			setSavingReferences(false)
		}
	}

	// Open modal for a document
	const openModal = (document) => {
		setActiveDocument(document)
		// Pre-populate with existing references if any
		const existingRefs = document.matched_bookings || []
		setTempReferences(existingRefs)
		setReferenceInput("")
		setReferenceError("")
	}

	// Close modal
	const closeModal = () => {
		setActiveDocument(null)
		setTempReferences([])
		setReferenceInput("")
		setReferenceError("")
	}

	// Toggle expanded bookings view for a document
	const toggleExpandedBookings = (documentId) => {
		setExpandedBookings((prev) => ({
			...prev,
			[documentId]: !prev[documentId],
		}))
	}

	// Delete document from storage and database
	const deleteDocument = async (document) => {
		try {
			// Delete file from Firebase Storage
			await storage.ref(document.file_path).delete()

			// Delete document from Firestore parsing_queue
			await db.collection("parsing_queue").doc(document.id).delete()

			// Also delete any corresponding documents from invoice_documents collection
			// These are created by the CRON job when matched_bookings exist
			const invoiceDocuments = await db.collection("invoice_documents").where("full_path", "==", document.file_path).get()

			// Delete all matching invoice documents
			const deletePromises = invoiceDocuments.docs.map((doc) => db.collection("invoice_documents").doc(doc.id).delete())
			await Promise.all(deletePromises)
		} catch (error) {
			console.error("Error deleting document:", error)
			alert("Error deleting document: " + error.message)
		}
	}

	// Show confirmation modal before deleting
	const confirmDelete = (document) => {
		showModal({
			type: "ERROR",
			title: "Delete Document?",
			body: `Are you sure you want to permanently delete "${document.file_name}"? This action cannot be undone.`,
			cancel: {
				label: "Cancel",
				action: () => {},
			},
			next: {
				label: "Delete",
				action: () => deleteDocument(document),
			},
		})
	}

	// Reset document status back to pending
	const resetDocumentStatus = async (document) => {
		setResettingDocumentId(document.id)

		try {
			// Delete any corresponding documents from invoice_documents collection
			const invoiceDocuments = await db.collection("invoice_documents").where("full_path", "==", document.file_path).get()

			// Delete all matching invoice documents
			const deletePromises = invoiceDocuments.docs.map((doc) => db.collection("invoice_documents").doc(doc.id).delete())
			await Promise.all(deletePromises)

			// Reset the parsing_queue document status and remove processed data
			await db.collection("parsing_queue").doc(document.id).update({
				status: "pending",
				error_message: firebase.firestore.FieldValue.delete(),
				processed_at: firebase.firestore.FieldValue.delete(),
				matched_bookings: firebase.firestore.FieldValue.delete(),
				title: firebase.firestore.FieldValue.delete(),
				parse_response: firebase.firestore.FieldValue.delete(),
			})
		} catch (error) {
			console.error("Error resetting document status:", error)
			alert("Error resetting status: " + error.message)
		} finally {
			setResettingDocumentId(null)
		}
	}

	// Get status badge component
	const getStatusBadge = (document) => {
		// Check if successfully parsed but no bookings matched
		if (document.status === "success" && (!document.matched_bookings || document.matched_bookings.length === 0)) {
			return (
				<Badge
					label="Missing Booking(s)"
					type="AMBER"
				/>
			)
		}

		switch (document.status) {
			case "pending":
				return <Badge label="Pending" />
			case "processing":
				return (
					<Badge
						label="Processing"
						type="INFO"
					/>
				)
			case "failed":
				return (
					<Badge
						label="Failed"
						type="NEGATIVE"
					/>
				)
			case "success":
				return (
					<Badge
						label="Success"
						type="POSITIVE"
					/>
				)
			default:
				return <Badge label="Unknown" />
		}
	}

	// Calculate pagination
	const totalPages = Math.ceil(documents.length / itemsPerPage)
	const startIndex = (currentPage - 1) * itemsPerPage
	const endIndex = startIndex + itemsPerPage
	const currentDocuments = documents.slice(startIndex, endIndex)

	// Generate array of page numbers to display
	const getPageNumbers = () => {
		const pages = []
		const maxPagesToShow = 10

		if (totalPages <= maxPagesToShow) {
			// Show all pages if there are 10 or fewer
			for (let i = 1; i <= totalPages; i++) {
				pages.push(i)
			}
		} else {
			// Show first 3, last 3, and pages around current page with ellipsis
			if (currentPage <= 4) {
				// Near the start
				for (let i = 1; i <= 7; i++) pages.push(i)
				pages.push("...")
				for (let i = totalPages - 2; i <= totalPages; i++) pages.push(i)
			} else if (currentPage >= totalPages - 3) {
				// Near the end
				for (let i = 1; i <= 3; i++) pages.push(i)
				pages.push("...")
				for (let i = totalPages - 6; i <= totalPages; i++) pages.push(i)
			} else {
				// In the middle
				for (let i = 1; i <= 3; i++) pages.push(i)
				pages.push("...")
				for (let i = currentPage - 1; i <= currentPage + 1; i++) pages.push(i)
				pages.push("...")
				for (let i = totalPages - 2; i <= totalPages; i++) pages.push(i)
			}
		}

		return pages
	}

	return (
		<>
			<Tile fullPage={true}>
				<Title>
					<h1>Invoice Parsing Queue</h1>
				</Title>

				<div className="parsing-queue-content">
					{/* File Drop Area */}
					<div
						className={`file-drop-area ${isDragOver ? "drag-over" : ""} ${uploadedFile ? "has-file" : ""}`}
						onDrop={handleDrop}
						onDragOver={handleDragOver}
						onDragLeave={handleDragLeave}>
						{!uploadedFile ? (
							<>
								<Upload
									size={32}
									color="#7f7f7f"
								/>
								<p>
									Drop the PDF invoice here or{" "}
									<label
										htmlFor="file-input"
										className="file-input-label">
										browse files
									</label>
								</p>
								<span className="file-format-hint">PDF files only</span>
								<input
									id="file-input"
									type="file"
									accept=".pdf,application/pdf"
									onChange={handleFileChange}
									style={{ display: "none" }}
								/>
							</>
						) : (
							<div className="uploaded-file-info">
								<div className="file-icon-and-info">
									<FileText
										size={24}
										color="#e74c3c"
									/>
									<div className="file-details">
										<div className="file-name">{uploadedFile.file_name}</div>
										<div className="file-meta">
											{formatFileSize(uploadedFile.file_size)} • PDF • Selected {new Date(uploadedFile.uploaded_at).toLocaleTimeString()}
										</div>
									</div>
								</div>
								<button
									className="remove-file-btn"
									onClick={removeFile}
									title="Remove file">
									<X size={16} />
								</button>
							</div>
						)}
					</div>

					{/* Upload Progress */}
					{isUploading && (
						<div className="upload-progress">
							<div className="progress-bar">
								<div
									className="progress-fill"
									style={{ width: `${uploadProgress}%` }}></div>
							</div>
							<span>Uploading... {uploadProgress}%</span>
						</div>
					)}

					{/* Upload Button */}
					{uploadedFile && !isUploading && (
						<div className="file-actions">
							<p className="upload-ready">✓ PDF selected & validated.</p>
							<Button
								small={true}
								label="Process Upload"
								onClick={uploadFile}
								disabled={isUploading}
								className="upload-btn"
							/>
						</div>
					)}

					{/* Documents Table */}
					<div className="table-section">
						<Table
							className="parsing-queue-table"
							headings={["Uploaded", "User", "Document Title", "Document Link", "Matched Booking(s)", "Status", ""]}
							noResults={documents.length === 0}
							noResultsMessage={"No documents in the queue yet"}>
							{currentDocuments.map((document) => (
								<tr
									key={document.id}
									data-system-id={document.id}>
									<td>{document.uploaded_at ? moment(document.uploaded_at.toDate()).format("DD/MM/YYYY HH:mm:ss") : "Processing..."}</td>
									<td>{agents[document.uploaded_by_uid] || document.uploaded_by_email || "Unknown User"}</td>
									<td>{document.title || document.file_name}</td>
									<td>
										<a
											href={document.file_url}
											target="_blank"
											rel="noopener noreferrer"
											className="document-link">
											View Document
										</a>
									</td>
									<td className="bookings-cell">
										{document.matched_bookings && document.matched_bookings.length > 0 ? (
											<div className="bookings-references-container">
												<div className="bookings-references-list">
													{(() => {
														const isExpanded = expandedBookings[document.id]
														const bookings = document.matched_bookings
														const displayBookings = isExpanded || bookings.length <= 1 ? bookings : bookings.slice(0, 1)

														return displayBookings.map((booking, index) => (
															<div
																key={index}
																className="booking-item">
																<a
																	href={`/booking/${booking.booking_id}`}
																	target="_blank"
																	rel="noopener noreferrer"
																	className="booking-link">
																	{booking.reference}
																	<ExternalLink size={12} />
																</a>
															</div>
														))
													})()}
												</div>
												{document.matched_bookings.length > 1 && (
													<button
														className="toggle-bookings-btn"
														onClick={() => toggleExpandedBookings(document.id)}>
														{expandedBookings[document.id] ? "Show less" : `View all ${document.matched_bookings.length} matches`}
													</button>
												)}
											</div>
										) : document.status === "success" ? (
											<Badge
												label="None"
												type="AMBER"
											/>
										) : (
											<span className="no-bookings">-</span>
										)}
									</td>
									<td>{getStatusBadge(document)}</td>
									<td className="is-button actions-cell">
										<div className="action-buttons">
											{document.status === "success" && (
												<Button
													small={true}
													label={document.matched_bookings && document.matched_bookings.length > 0 ? "Edit" : "Add"}
													onClick={() => openModal(document)}
												/>
											)}
											{document.status !== "pending" && (
												<Button
													small={true}
													icon={<RotateCcwIcon size={14} />}
													onClick={() => resetDocumentStatus(document)}
													title="Reset to pending"
													loading={resettingDocumentId === document.id}
													disabled={resettingDocumentId === document.id}
												/>
											)}
											<Button
												small={true}
												icon={<Trash2Icon size={14} />}
												onClick={() => confirmDelete(document)}
												title="Delete document"
											/>
										</div>
									</td>
								</tr>
							))}
						</Table>

						{/* Pagination */}
						<div className="pagination">
							<button
								className="pagination-arrow"
								onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
								disabled={currentPage === 1 || totalPages === 0}>
								← Previous
							</button>

							{getPageNumbers().map((page, index) => {
								if (page === "...") {
									return (
										<span
											key={`ellipsis-${index}`}
											className="pagination-ellipsis">
											...
										</span>
									)
								}
								return (
									<button
										key={page}
										className={`pagination-number ${currentPage === page ? "active" : ""}`}
										onClick={() => setCurrentPage(page)}
										disabled={totalPages === 0}>
										{page}
									</button>
								)
							})}

							<button
								className="pagination-arrow"
								onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
								disabled={currentPage === totalPages || totalPages === 0}>
								Next →
							</button>
						</div>
					</div>
				</div>
			</Tile>

			{/* Booking References Modal */}
			{activeDocument && (
				<Window
					title={activeDocument.matched_bookings && activeDocument.matched_bookings.length > 0 ? "Edit Booking References" : "Add Booking References"}
					subtitle={`Document: ${activeDocument.file_name}`}
					className="booking-references-modal"
					close={closeModal}>
					{/* Input Row */}
					<div className="reference-input-row">
						<Input
							type="text"
							placeholder="Enter 7-digit reference"
							value={referenceInput}
							onChange={setReferenceInput}
							error={referenceError}
							onKeyPress={(e) => {
								if (e.key === "Enter") {
									addReferenceToList()
								}
							}}
						/>
						<Button
							icon={<Plus size={16} />}
							xsSmall={true}
							onClick={addReferenceToList}
							disabled={validatingReference}
							loading={validatingReference}
						/>
					</div>

					{/* Added References List */}
					{tempReferences.length > 0 && (
						<div className="added-references-list">
							{tempReferences.map((ref, index) => (
								<div
									key={index}
									className="reference-item">
									<span>GHD-{ref.reference}</span>
									<button
										className="remove-ref-btn"
										onClick={() => removeReferenceFromList(ref.reference)}>
										<Trash2Icon size={14} />
									</button>
								</div>
							))}
						</div>
					)}

					{/* Action Buttons */}
					<div className="modal-actions">
						<Button
							label="Cancel"
							type="OUTLINE"
							small={true}
							onClick={closeModal}
						/>
						<Button
							label="Save References"
							small={true}
							onClick={() => saveBookingReferences(activeDocument.id)}
							disabled={savingReferences || tempReferences.length === 0}
							loading={savingReferences}
						/>
					</div>
				</Window>
			)}
		</>
	)
}
